import {
  Input,
  OnInit,
  OnDestroy, 
  ChangeDetectorRef, 
  Directive,
  Injectable,
  Output,
  EventEmitter,
  inject,
  SimpleChanges,
  OnChanges
} from "@angular/core";
import {
  AsyncValidatorFn,
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormControlStatus,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from "@angular/forms";
import { BaseFpxDataService } from "../models/index";
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  Observable,
  of,
  startWith,
  Subject,
  Subscription,
  switchMap,
} from "rxjs";
import { LookUpFn } from "../models/http-provider.model"; 
import { FpxFormAnchorDirective } from '../directives/error-handler/error-container.directive';
import { FPxFakeFormControlErrorMessageService } from "../services/form-control-error/form-control-error.service";

@Directive()
export abstract class BaseFpxControlComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  /** @description Input Output variables */
  @Input() changePublisher!: Subject<{
    subject: string;
    payload: any;
  }>;
  
  @Input() formControlKey: string = "";
  @Input() formControlName!: string;
  @Input() label: string = "";
  @Input() placeholder: string = "";
  @Input() dependencyField: string[] = [];
  @Input() controlName: string = "";
  @Input() selectableList$: Observable<Array<any>> = of([]);
  @Input() ngTemplateName!: string;
  @Output() commonControlEvent : EventEmitter<{eventName : string , payload : any}> = new EventEmitter<{eventName : string , payload : any}> ()
  @Input() hint: string = "";
  @Input() controlValue: any;
  protected domainControlValue: any;
  private _registeredDependencies: string[] = [];
  private _dropdownMode: "BASIC" | "SEARCHABLE" = "BASIC";
  private readonly changeDetectorRef: ChangeDetectorRef;

  /** @description Template variables */
  selectableDataList$!: Observable<any>;
  formControl!: FormControl;
  loading: boolean = false;
  errorCode: string = "";
  private readonly input$ = new Subject<any>();

  inputChange$ = this.input$.asObservable();

  /** @description Private Variables */
  private readonly _syncValidators: ValidatorFn[] = [];
  private readonly _asyncValidators: AsyncValidatorFn[] = [];
  private readonly notifyContext: Map<string, any> = new Map<string, any>();
  private readonly _controlContainer: ControlContainer;
  private changePublisher$!: Subscription;
  private controlStatus$!: Subscription;
  private _dataService!: BaseFpxDataService<unknown>;
  private _helper!: BaseFpxControlHelper;
  public readonly errorMessageService : FPxFakeFormControlErrorMessageService = inject(FPxFakeFormControlErrorMessageService);
  public readonly formAnchorDirective : FpxFormAnchorDirective = inject(FpxFormAnchorDirective);

  constructor(
    controlContainer: ControlContainer,
    changeDetectorRef: ChangeDetectorRef
  ) {
    this._controlContainer = controlContainer;
    this.changeDetectorRef = changeDetectorRef;
  }
  protected setHelper(helper: BaseFpxControlHelper) {
    this._helper = helper;
  }

  /** @description protected members */
  protected addSyncValidatorFn(syncValidatorFn: ValidatorFn) {
    this._syncValidators.push(syncValidatorFn);
  }

  protected addAsyncValidatorFn(asyncValidatorFn: AsyncValidatorFn) {
    this._asyncValidators.push(asyncValidatorFn);
  }

  protected setSelectableData(selectableDataList: LookUpFn<any>) {
    this.selectableDataList$ = selectableDataList().pipe(
      map((res) => [...res])
    );
    this.changeDetectorRef.detectChanges();
  }

  protected addDependencies(dependencyList: string[]) {
    this._registeredDependencies = dependencyList;
  }

  protected setDataSource(service: BaseFpxDataService<unknown>): void {
    this._dataService = service;
  }
  protected setDropdownMode(mode: "BASIC" | "SEARCHABLE"): void {
    this._dropdownMode = mode;
  }

  /** @description init lifecycle hook */
  ngOnInit(): void {
    if (this.controlName) {
      this.formControlName = this.controlName;
    }
    let formGroup = this._controlContainer.control as FormGroup;
    this.formControl = formGroup.controls[this.formControlName] as FormControl;
    this.doPreInit();
    this.formControl.addValidators(this._syncValidators);
    this.formControl.addAsyncValidators(this._asyncValidators);
    this.formControl.updateValueAndValidity();
    this._setHelperDependency();
  }

  private _setHelperDependency(){
    if(this._helper){
        this._helper.setFormName(this.formAnchorDirective?.formName)
        this._helper.setFormControlKey(this.formControlKey)
    }
  
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["controlValue"]?.currentValue !==
      changes["controlValue"]?.previousValue
    ) {
      this.customWriteValue(changes["controlValue"].currentValue);
    }
  }
  ngAfterViewInit() { 
    if (this.changePublisher) {
      this.changePublisher$ = this.changePublisher
        .asObservable()
        .subscribe((response) => {
          this.notifyContext.set(response.subject, response.payload);
          if (this.dependencyField.includes(response.subject)) {
            let dependentFieldValue: any = {};
            for (let index = 0; index < this.dependencyField.length; index++) {
              dependentFieldValue[this._registeredDependencies[index]] =
                this.notifyContext.has(this.dependencyField[index])
                  ? this.notifyContext.get(this.dependencyField[index])
                  : null;
            }
            this.doChangeNotify(response.subject, dependentFieldValue);
            this._helper?.doChangeNotify(response.subject, dependentFieldValue);
            this.changeDetectorRef.markForCheck();
            if (
              this._dropdownMode === "BASIC" &&
              this.dependencyField.length > 0
            ) {
              this._refereshDataBasic(dependentFieldValue);
            } else if (
              this._dropdownMode === "SEARCHABLE" &&
              this.dependencyField.length > 0
            ) {
              this._refereshDataSearchable(dependentFieldValue);
            }
          }
        });
    }

    this.controlStatus$ = this.formControl.statusChanges.subscribe(
      (res: FormControlStatus) => {
        if (res === "INVALID" || this.formControl.errors) {
          this.errorCode = this.getErrorCode();
          if(!this.errorCode) this.errorCode = this.formControl?.errors?.errorMessage;
          console.log("error identified", this.formControlName, this.errorCode);
          this.changeDetectorRef.markForCheck();
        } 
        else {
          this.errorCode = "";
        }
        if (res === "PENDING") {
          this.loading = true;
        } else {
          this.loading = false;
        }
      }
    );
    if (this._dropdownMode === "BASIC" && this.dependencyField.length === 0) {
      this._refereshDataBasic(null);
    } else if (
      this._dropdownMode === "SEARCHABLE" &&
      this.dependencyField.length === 0
    ) {
      this._refereshDataSearchable(null);
    }

    if (this._dropdownMode === "SEARCHABLE") {
      console.log("subscribing to value changes");
      this.formControl.valueChanges.subscribe((value: any) => {
        console.log("Change in value", value, typeof value);
        if (typeof value === "string" && value !== "") {
          const selectData$ =
            this._helper &&
            this._helper?.doPostLookupInterceptor(
              this._dataService.lookup({ searchText: value })(),
              { searchText: value }
            )
              ? () =>
                  this._helper?.doPostLookupInterceptor(
                    this._dataService.lookup({ searchText: value })(),
                    { searchText: value }
                  )
              : this._dataService.lookup({ searchText: value });
          this.setSelectableData(selectData$);
        } else {
          this.onChange(value);
        }
      });
    } else {
      this.formControl.valueChanges.subscribe((value: any) => {
        this.changeDetectorRef.markForCheck();
      });
    }
    this._helper?.setControl(this.formControl);
    this._helper?.doPreInit();
    this.doPostInit();
    this._helper?.doPostInit();
  }

  private _refereshDataBasic(key: { [k: string]: any } | null) {
    try {
      if (this._dataService && this._dataService.lookup) {
        let keys: { [k: string]: any } = {};
        if (key) {
          for (let i = 0; i < this._registeredDependencies.length; i++) {
            keys[this._registeredDependencies[i]] =
              key[this._registeredDependencies[i]];
          }
        }
        const selectData$ =
        this._helper &&
        this._helper?.doPostLookupInterceptor(
          this._dataService.lookup(keys)(),
          key
        )
          ? () =>
              this._helper?.doPostLookupInterceptor(
                this._dataService.lookup(keys)(),
                key
              )
          : this._dataService.lookup(keys);
      this.setSelectableData(selectData$);
      this.changeDetectorRef.markForCheck();
      }
    } catch (error) {
      console.error("Data Service not implemented");
    }
  }

  private _refereshDataSearchable(key: { [k: string]: any } | null) {
    try {
      if (this._dataService) {
        const selectData$ =
        this._helper &&
        this._helper?.doPostLookupInterceptor(
          this._loadDynamicDropdown()(),
          key
        )
          ? () =>
              this._helper?.doPostLookupInterceptor(
                this._loadDynamicDropdown()(),
                key
              )
          : this._loadDynamicDropdown();
      this.setSelectableData(selectData$);
      this.changeDetectorRef.markForCheck();
      }
    } catch (error) {
      console.error("Data Service not implemented");
    }
  }

  private _loadDynamicDropdown() {
    return () => {
      return this.inputChange$.pipe(
        distinctUntilChanged(),
        startWith(""),
        debounceTime(200),
        switchMap((value) => {
          this.loading = true;
          return this._dataService
            .lookup({ searchText: value })()
            .pipe(
              finalize(() => {
                this.loading = false;
                /** loading spinner need to handle here */
              })
            );
        })
      );
    };
    /*this.inputChange$.pipe(
      distinctUntilChanged(),
      startWith(''),
      debounceTime(200),
      switchMap((value) => {
        this.loading = true;
        return this._dataService
          .lookup(criteria)()
          .pipe(
            finalize(() => {
              this.loading = false;
            })
          );
      })
    );*/
  }

  /** @description destroy lifecycle hook */
  ngOnDestroy(): void {
    if (this.changePublisher$) {
      this.changePublisher$.unsubscribe();
    }
    if (this.controlStatus$) {
      this.controlStatus$.unsubscribe();
    }
    this.notifyContext.clear();
    this.doDestroy();
  }

  /** @description return the error code */
  private getErrorCode() {
    // return Object.keys(this.formControl.errors as ValidationErrors)[0];
    const validator = Object.keys(this.formControl.errors as ValidationErrors)[0];
    let message = '';
    if (this.formAnchorDirective && this.formAnchorDirective.formName) {
      message =  this.errorMessageService.getErroMessage()?.[
        this.formAnchorDirective.formName
      ][`${this.formControlName}_${validator}`];
    } else{
      message = this.errorMessageService?.getErroMessage()?.["common"]?.[validator];
    }
     return message;
  }

  /** @description control vaule accessor */
  onChange: any = () => {};

  onTouched: () => void = () => {};

  setDisabledState?(isDisabled: boolean): void {
    console.error("isdisable", this.formControlName, isDisabled);
  }

  registerOnChange(fn: any): void {
    console.log(this.formControlName, "registerchange");
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(obj: any): void {
    // if (this.formControl && obj != this.formControl.value) {
    //   this.formControl.setValue(obj, { emitEvent: false });
    // }
    if (this.formControl) {
      this.domainControlValue = obj;
      this.changeDetectorRef.markForCheck();
    }
  }

  public refreshState(): void {
    this.doRefresh();
  }

  public onInput(value: any) {
    this.input$.next(value);
  }

  protected onItemSelect(value: { id: string; text: string }): void {
    this.onChange(value);
    this.onTouched();
  }

  protected itemDisplay(value: { id: string; text: string }): string {
    return value && value.text ? value.text : "";
  }

  /** @description override methods to access the angular life cycle hooks */
  protected doPreInit(): void {}
  protected doPostInit(): void {}
  protected doChangeNotify(subject: string, payload: any): void {}
  protected doDestroy(): void {}
  protected doRefresh(): void {}
  protected customWriteValue(payload: any): void {}
}
@Injectable()
export abstract class BaseFpxControlHelper {
  protected formControl!: FormControl;
  private _formName : string= '';
  private _formControlKey:string = '';
  constructor() {}

  public setControl(control: FormControl) {
    this.formControl = control;
  }

  public doPostLookupInterceptor(
    data: Observable<any>,
    key: any
  ): Observable<any> {
    return data;
  }

  public doChangeNotify(subject: string, payload: any): void {}

  public doPreInit(): void {}

  public doPostInit() : void {}

  public setFormName(formName:string){
    this._formName = formName
  }

  public get getFormName():string{
    return this._formName
  }
  public setFormControlKey(formControlKey:string){
    this._formControlKey = formControlKey
  }

  public get getFormControlKey():string{
    return this._formControlKey
  }
}
